import React, { useRef } from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import {
    type UploadTransactionsFormLogicDependencies,
    UploadTransactionsFormLogic,
    type UploadTransactionsFormLogicState,
    DATE_FORMATS,
    CURRENCIES,
    DECIMAL_SEPARATORS,
} from './logic'
import { Button } from '~common/components/button'
import { LoadingIndicator } from '~common/components/loading'
import { TextField } from '~common/components/text-field'
import { RadioGroupField } from '~common/components/radio-group'
import { ErrorBox } from '~/features/error-box'

interface UploadTransactionsFormProps {
    onClose: () => void
}

export function UploadTransactionsForm({
    onClose,
}: UploadTransactionsFormProps) {
    const appContext = useAppContext()
    const { state, logic } = useLogic(UploadTransactionsFormLogic, {
        onClose,
        ...appContext,
    })

    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null
        if (selectedFile) {
            logic.handleFileUpload(selectedFile)
        }
    }

    if (state.loadState === 'running') {
        const loadingText =
            state.loadingStage === 'importing'
                ? 'Importing Transactions'
                : state.loadingStage === 'matching'
                  ? 'Matching Invoices'
                  : 'Processing file...'
        return (
            <Container>
                <LoadingIndicator size="medium" />
                <span>{loadingText}</span>
            </Container>
        )
    }

    return (
        <FormContainer stage={state.parsedCSV ? 'preview' : 'upload'}>
            <Header>
                <Title>Upload Bank Transactions</Title>
            </Header>

            <Section>
                <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Button
                    label={
                        state.file ? `${state.file.name}` : 'Select CSV or Excel File'
                    }
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                />
            </Section>

            {state.parsedCSV ? (
                <>
                    <Section>
                        <SubTitle>Bank Account Name *</SubTitle>
                        <TextField
                            editable
                            controlledValue={state.accountName}
                            onChange={(v) => logic.updateAccountName(v)}
                            placeholder="e.g., My Checking Account"
                        />
                    </Section>

                    <Section>
                        <SubTitle>Bank Account IBAN *</SubTitle>
                        <TextField
                            editable
                            controlledValue={state.iban}
                            onChange={(v) => logic.updateIban(v)}
                            placeholder="e.g., CH9300762011623852957"
                        />
                    </Section>

                    <Section>
                        <SubTitle>Input Date Format *</SubTitle>
                        <TextField
                            editable
                            controlledValue={state.dateFormat}
                            onChange={(v) =>
                                logic.updateDateFormat(
                                    v as (typeof DATE_FORMATS)[number],
                                )
                            }
                            dropdown
                            options={DATE_FORMATS as unknown as string[]}
                        />
                    </Section>

                    <Section>
                        <SubTitle>Input Currency *</SubTitle>
                        <TextField
                            editable
                            controlledValue={state.currency}
                            onChange={(v) =>
                                logic.updateCurrency(
                                    v as (typeof CURRENCIES)[number],
                                )
                            }
                            dropdown
                            options={CURRENCIES as unknown as string[]}
                        />
                    </Section>

                    <Section>
                        <SubTitle>Decimal Separator *</SubTitle>
                        <RadioGroupField
                            items={{
                                dot: 'Dot (1.23)',
                                comma: 'Comma (1,23)',
                                cents: 'Value in cents (123)',
                            }}
                            value={state.decimalSep}
                            onChange={(v) =>
                                logic.updateDecimalSep(
                                    v as (typeof DECIMAL_SEPARATORS)[number],
                                )
                            }
                            size="medium"
                            ariaLabel="Decimal Separator"
                        />
                    </Section>

                    <Section>
                        <SubTitle>Preview (first 20 rows)</SubTitle>
                        <PreviewContainer>
                            <PreviewTable>
                                <thead>
                                    <tr>
                                        {state.parsedCSV.headers.map(
                                            (header, idx) => (
                                                <th key={idx}>{header}</th>
                                            ),
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {state.parsedCSV.rows.map((row, rowIdx) => (
                                        <tr key={rowIdx}>
                                            {row.map((cell, cellIdx) => (
                                                <td key={cellIdx}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </PreviewTable>
                        </PreviewContainer>
                    </Section>

                    <Section>
                        <SubTitle>Field Mapping</SubTitle>
                        <MappingGrid>
                            {INTERNAL_FIELDS.map((field) => (
                                <InnerFieldContainer key={field}>
                                    <Label>
                                        {field === 'vendorName'
                                            ? 'Vendor Name'
                                            : field === 'counterpartyIban'
                                              ? 'Counterparty IBAN'
                                              : field.charAt(0).toUpperCase() +
                                                field
                                                    .slice(1)
                                                    .replace(/([A-Z])/g, ' $1')}
                                        {REQUIRED_FIELDS_MAPPING.includes(
                                            field as (typeof REQUIRED_FIELDS_MAPPING)[number],
                                        )
                                            ? ' *'
                                            : ''}
                                    </Label>
                                    <TextField
                                        editable
                                        dropdown
                                        controlledValue={
                                            state.fieldMapping[field] || ''
                                        }
                                        onChange={(v) =>
                                            logic.updateFieldMapping(
                                                field,
                                                v || null,
                                            )
                                        }
                                        options={[
                                            ...(state.parsedCSV?.headers || []),
                                        ]}
                                        placeholder="Select column"
                                    />
                                </InnerFieldContainer>
                            ))}
                        </MappingGrid>
                    </Section>
                    <Actions>
                        {state.errorMessage && (
                            <div
                                style={{ marginRight: 'auto', maxWidth: '60%' }}
                            >
                                <ErrorBox errorMessage={state.errorMessage} />
                            </div>
                        )}
                        {!state.canSubmit && !state.errorMessage ? (
                            <div
                                style={{ marginRight: 'auto', maxWidth: '60%' }}
                            >
                                <ErrorBox
                                    errorMessage={(() => {
                                        const missing =
                                            logic.getMissingRequiredFields()
                                        return missing.length > 0
                                            ? `Please fill out: ${missing.join(', ')}`
                                            : 'Please fill out all required fields'
                                    })()}
                                />
                            </div>
                        ) : undefined}
                        {state.parsedCSV && (
                            <Button
                                label="Upload"
                                variant="primary"
                                onClick={() => logic.submitForm()}
                                disabled={!state.canSubmit}
                            />
                        )}
                    </Actions>
                </>
            ) : undefined}
        </FormContainer>
    )
}

const INTERNAL_FIELDS = [
    'date',
    'amount',
    'vendorName',
    'counterpartyIban',
    'referenceText',
    'note',
] as const

const REQUIRED_FIELDS_MAPPING = ['date', 'amount', 'vendorName'] as const

const FormContainer = styled.div<{ stage: 'upload' | 'preview' }>`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    // prettier-ignore
    padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) =>
        theme.spacing[24]};
    box-sizing: border-box;
    gap: ${({ theme }) => theme.spacing[6]};
    overflow-y: auto;

    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }

    ${({ stage }) =>
        stage === 'upload' &&
        `
        height: 100%;
        justify-content: center;
        align-items: center;
        gap: ${({ theme }) => theme.spacing[4]};
    `}
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
        margin: 0;
        font-size: ${({ theme }) => theme.typography.fontSize.lg};
    }
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
`

const PreviewContainer = styled.div`
    max-height: 300px;
    overflow-y: auto;
    border-radius: ${({ theme }) => theme.borderRadius.md};
`

const PreviewTable = styled.table`
    border-collapse: collapse;
    width: 100%;

    th,
    td {
        border: 1px solid ${({ theme }) => theme.colors.border.primary};
        padding: ${({ theme }) => theme.spacing[2]};
        text-align: left;
        white-space: nowrap;
    }

    thead {
        // no sticky or background
    }

    tbody {
        // normal
    }
`

const Label = styled.div``

const Title = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    margin: 0;
`

const SubTitle = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize['xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.secondary};
    margin-top: ${({ theme }) => theme.spacing[2]};
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: ${({ theme }) => theme.spacing[4]};
`

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing[4]};
    margin-top: auto;
`

const MappingGrid = styled.div`
    display: flex;

    flex-direction: column;

    gap: ${({ theme }) => theme.spacing[4]};
`

const InnerFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`
